import React, { useState, useEffect } from 'react';
import { Row, Col, Spin, Empty, Pagination, Select, Input, Button, Space, Typography } from 'antd';
import { SearchOutlined, FilterOutlined, SortAscendingOutlined } from '@ant-design/icons';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const MarketplaceGrid = ({
  items = [],
  renderItem,
  loading = false,
  emptyMessage = "No items found",
  showPagination = true,
  showFilters = true,
  showSearch = true,
  showSort = true,
  itemsPerPage = 12,
  gridProps = {
    xs: 24,
    sm: 12,
    md: 8,
    lg: 6,
    xl: 6
  },
  onSearch,
  onFilter,
  onSort,
  onPageChange,
  searchPlaceholder = "Search...",
  sortOptions = [
    { value: 'name', label: 'Name A-Z' },
    { value: 'price', label: 'Price Low to High' },
    { value: 'rating', label: 'Rating' },
    { value: 'newest', label: 'Newest' }
  ],
  filterOptions = [],
  currentPage = 1,
  totalItems = 0,
  showCarousel = false,
  carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  }
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filters, setFilters] = useState({});

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSort = (value) => {
    setSortBy(value);
    if (onSort) {
      onSort(value);
    }
  };

  const handleFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const handlePageChange = (page) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const renderGrid = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description={emptyMessage} />
        </div>
      );
    }

    if (showCarousel) {
      return (
        <Slider {...carouselSettings}>
          {items.map((item, index) => (
            <div key={item.id || index} style={{ padding: '0 8px' }}>
              {renderItem(item, index)}
            </div>
          ))}
        </Slider>
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {items.map((item, index) => (
          <Col key={item.id || index} {...gridProps}>
            {renderItem(item, index)}
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div className="marketplace-grid">
      {/* Header with Search, Filters, and Sort */}
      {(showSearch || showFilters || showSort) && (
        <div style={{ 
          marginBottom: '24px', 
          padding: '16px', 
          background: '#fafafa', 
          borderRadius: '8px',
          border: '1px solid #f0f0f0'
        }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Search */}
            {showSearch && (
              <Col xs={24} sm={12} md={8}>
                <Search
                  placeholder={searchPlaceholder}
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
            )}

            {/* Filters */}
            {showFilters && filterOptions.length > 0 && (
              <Col xs={24} sm={12} md={8}>
                <Space wrap>
                  {filterOptions.map((filter) => (
                    <Select
                      key={filter.key}
                      placeholder={filter.placeholder}
                      style={{ minWidth: 120 }}
                      allowClear
                      onChange={(value) => handleFilter(filter.key, value)}
                    >
                      {filter.options.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  ))}
                </Space>
              </Col>
            )}

            {/* Sort */}
            {showSort && (
              <Col xs={24} sm={12} md={8}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Select
                    value={sortBy}
                    style={{ minWidth: 150 }}
                    onChange={handleSort}
                    suffixIcon={<SortAscendingOutlined />}
                  >
                    {sortOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            )}
          </Row>
        </div>
      )}

      {/* Grid Content */}
      {renderGrid()}

      {/* Pagination */}
      {showPagination && totalItems > itemsPerPage && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '32px',
          padding: '16px 0'
        }}>
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={itemsPerPage}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) => 
              `${range[0]}-${range[1]} of ${total} items`
            }
          />
        </div>
      )}
    </div>
  );
};

export default MarketplaceGrid;
